import { createAction } from 'redux-actions';
import get from 'lodash/get';
import pick from 'lodash/pick';
import { push } from 'connected-react-router';

import * as AccountService from '@app/services/accountService';
import {
  getAvailableOptions, setSelectedOptionId, setPromoCode,
} from '@app/actions/subscribeActions';
import { detectUser } from '@app/actions/userActions';
import { requestRecaptureUpdate } from '@app/actions/recaptchaActions';
import { getAlphaLangCode } from '@app/selectors/localiztionSelectors';

import RouterConstants from '@app/constants/router';

export const setAccountDetails = createAction('ACCOUNT/SET_ACCOUNT_DETAILS');
export const setAccountError = createAction('ACCOUNT/SET_ERROR');
export const setCountries = createAction('ACCOUNT/SET_COUNTRIES');
export const setLoadingDataStatus = createAction('ACCOUNT/SET_LOADING_DATA_STATUS');
export const setAccountValidationParams = createAction('USER/SET_ACCOUNT_VALIDATION_PARAMS');

export function getAccountDetails() {
  return (dispatch, getState) => {
    const { user: { authToken } } = getState();
    dispatch(setLoadingDataStatus(true));

    return AccountService.getAccountDetails(authToken)
      .then((details) => {
        dispatch(setAccountDetails(details));
      })
      .catch((error) => {
        console.error('Error on getting account details: ', error);
        dispatch(setAccountError(error));
      })
      .finally(() => dispatch(setLoadingDataStatus(false)));
  };
}

export function getCountries() {
  return (dispatch, getState) => {
    const { account: { countries = [] } } = getState();

    if (countries.length) {
      return;
    }

    AccountService.getCountries({ languageId: getAlphaLangCode(getState()) })
      .then((countries) => {
        dispatch(setCountries(countries));
      })
      .catch((error) => {
        console.error('Error on getting countries: ', error);
        dispatch(setAccountError(error));
      });
  };
}

export function updateAccountData(formData) {
  return (dispatch, getState) => {
    const state = getState();
    const { user: { authToken } } = state;

    const accountDetailsParams = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      emailConfirm: formData.email,
      allowNews: formData.allowNews,
      additionalInformation: {
        shareEmail: formData.shareEmail,
      },
      phoneNumber: formData.phoneNumber || '',
      languageId: formData.languageId,
    };

    const addressParams = pick(formData, [
      'addressCity',
      'addressCountry',
      'addressStreet',
      'addressZip',
    ]);

    return AccountService.updateAccountPersonalDetails(accountDetailsParams, authToken)
      .then(() => AccountService.updateAccountAddressDetails(addressParams, authToken))
      .catch((error) => {
        console.error('Error on updating account', error);
        dispatch(setAccountError(error));
      });
  };
}

export function userSignUp(formData) {
  return (dispatch, getState) => {
    const state = getState();

    const params = {
      ...formData,
      languageId: getAlphaLangCode(state),
      brandId: 1,
      allowNews: !!formData.allowNews,
      additionalInformation: {
        optionId: state.subscribe.selectedOptionId,
        promoCode: state.subscribe.promoCode,
        shareEmail: !!formData.shareEmail,
      },
    };

    return AccountService.signUp(params)
      .then(() => dispatch(push(RouterConstants.SIGN_UP_SUCCESS)))
      .catch((error) => {
        dispatch(requestRecaptureUpdate());
        console.log('Error on account creation', error);
        dispatch(setAccountError(error));
      });
  };
}

/**
 * @param {Object} params
 * @param {String} params.email - user email
 * @param {String} params.idHash - user idHash
 * @param {String} params.token - user token
 * @return {Function}
 */
export function activateAccount(params) {
  return (dispatch, getState) => {
    AccountService.activateAccount(params)
      .then(() => {
        detectUser(dispatch);
        return dispatch(getAccountDetails());
      })
      .then(() => {
        const promoCode = get(getState(), 'account.details.additionalInformation.promoCode', null);
        dispatch(setPromoCode(promoCode));
        return dispatch(getAvailableOptions({ voucherCode: promoCode }));
      })
      .then(() => {
        const state = getState();
        const selectedPackageId = get(state, 'account.details.additionalInformation.optionId');

        return dispatch(setSelectedOptionId(selectedPackageId));
      })
      .catch((error) => {
        console.log('Error on account activation', error);
        dispatch(setAccountError(error));
      });
  };
}
